import React, { useState, useEffect, useRef } from 'react';
import { MENU_ITEMS, languages } from './constants/menu';
import { createSpeechRecognition, startRecording as startSpeechRecording, stopRecording as stopSpeechRecording } from './utils/speechRecognition';
import { generateOrderPDF } from './utils/pdfGenerator';
import { processOrderWithAI, fetchModels } from './services/orderService';
import Header from './components/Header';
import TabNavigation from './components/TabNavigation';
import OrderControls from './components/OrderControls';
import MenuManagement from './components/MenuManagement';
import OrderDisplay from './components/OrderDisplay';

function App() {
  const [transcript, setTranscript] = useState('');
  const [orderData, setOrderData] = useState(null);
  const [listening, setListening] = useState(false);
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('en-US');
  const [loading, setLoading] = useState(false);
  const [orderNumber, setOrderNumber] = useState(1);
  const [activeTab, setActiveTab] = useState('order');
  const [menuItems, setMenuItems] = useState(MENU_ITEMS);
  const [editingOrder, setEditingOrder] = useState(false);
  const [editedItems, setEditedItems] = useState([]);
  const recognitionRef = useRef(null);
  const processedResultsRef = useRef(0);

  useEffect(() => {
    async function loadModels() {
      const modelData = await fetchModels();
      setModels(modelData);
      if (modelData?.length > 0) {
        setSelectedModel(modelData[0].id);
      }
    }

    loadModels();
  }, []);

  const handleStartRecording = () => {
    // Clear previous transcript when starting new recording
    setTranscript('');
    setOrderData(null);
    processedResultsRef.current = 0;

    const recognition = createSpeechRecognition({
      language: selectedLanguage,
      onResult: (event) => {
        console.log('Speech recognition result:', event);
        let finalTranscript = '';
        let interimTranscript = '';
        
        // Only process new results to avoid repetition
        for (let i = processedResultsRef.current; i < event.results.length; ++i) {
          const result = event.results[i];
          const transcript = result[0].transcript;
          const confidence = result[0].confidence;
          
          console.log(`Transcript: "${transcript}" (confidence: ${confidence})`);
          
          if (result.isFinal) {
            finalTranscript += transcript + ' ';
            console.log(`Final transcript: "${transcript}" (confidence: ${confidence})`);
          } else {
            interimTranscript += transcript;
          }
        }
        
        // Update the processed results counter
        processedResultsRef.current = event.results.length;
        
        // Update transcript with new content only
        if (finalTranscript || interimTranscript) {
          setTranscript(prev => {
            const newTranscript = prev + finalTranscript + interimTranscript;
            console.log('Updated transcript:', newTranscript);
            return newTranscript;
          });
        }
      },
      onError: (event) => {
        console.error('Speech recognition error:', event.error);
        if (event.error !== 'no-speech') {
          alert(`Speech recognition error: ${event.error}`);
        }
        setListening(false);
      },
      onEnd: () => {
        console.log('Speech recognition ended');
        setListening(false);
      },
      onStart: () => {
        console.log('Speech recognition started');
      },
      onAudioStart: () => {
        console.log('Audio capturing started');
      },
      onAudioEnd: () => {
        console.log('Audio capturing ended');
      },
      onSoundStart: () => {
        console.log('Sound detected');
      },
      onSoundEnd: () => {
        console.log('Sound ended');
      },
      onSpeechStart: () => {
        console.log('Speech started');
      },
      onSpeechEnd: () => {
        console.log('Speech ended');
      }
    });

    try {
      startSpeechRecording(recognition);
      recognitionRef.current = recognition;
      setListening(true);
    } catch (error) {
      console.error('Error starting speech recognition:', error);
      alert('Error starting speech recognition. Please check microphone permissions.');
      setListening(false);
    }
  };

  const handleStopRecording = () => {
    stopSpeechRecording(recognitionRef.current);
    setListening(false);
  };

  const handleProcessOrder = async () => {
    if (!selectedModel || !transcript) return;

    setLoading(true);
    try {
      const parsed = await processOrderWithAI(transcript, selectedModel, menuItems);
      setOrderData(parsed);
    } catch (err) {
      console.error("Order processing error:", err);
      setOrderData({ order_summary: "Error processing order", items: [], total: 0, special_instructions: "" });
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePDF = async () => {
    if (!orderData) return;

    try {
      generateOrderPDF(orderData, orderNumber, menuItems);

      // Increment order number for next order
      setOrderNumber(prev => prev + 1);
      
      // Clear current order
      setTranscript('');
      setOrderData(null);
      setEditingOrder(false);
      setEditedItems([]);
      
      alert('Order sent to kitchen! PDF receipt saved.');
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating receipt. Please try again.');
    }
  };

  const addMenuItem = () => {
    const newItem = {
      name: window.prompt('Enter item name:'),
      price: parseFloat(window.prompt('Enter item price:')),
      options: window.prompt('Enter options (comma-separated):').split(',').map(opt => opt.trim())
    };
    
    if (newItem.name && newItem.price) {
      const key = newItem.name.toLowerCase().replace(/\s+/g, '_');
      setMenuItems(prev => ({
        ...prev,
        [key]: newItem
      }));
    }
  };

  const removeMenuItem = (itemKey) => {
    if (window.confirm('Are you sure you want to remove this item?')) {
      setMenuItems(prev => {
        const newMenu = { ...prev };
        delete newMenu[itemKey];
        return newMenu;
      });
    }
  };

  const startOrderEditing = () => {
    setEditingOrder(true);
    setEditedItems(orderData.items || []);
  };

  const updateItemQuantity = (index, newQuantity) => {
    if (newQuantity > 0) {
      setEditedItems(prev => {
        const newItems = [...prev];
        newItems[index] = { ...newItems[index], quantity: newQuantity };
        return newItems;
      });
    }
  };

  const removeItemFromOrder = (index) => {
    setEditedItems(prev => prev.filter((_, i) => i !== index));
  };

  const saveOrderChanges = () => {
    const updatedOrder = {
      ...orderData,
      items: editedItems,
      total: editedItems.reduce((sum, item) => sum + item.price, 0)
    };
    setOrderData(updatedOrder);
    setEditingOrder(false);
  };

  const cancelOrderEditing = () => {
    setEditingOrder(false);
    setEditedItems([]);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-950 via-purple-900 to-pink-900 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-40 right-32 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute bottom-32 left-1/3 w-80 h-80 bg-pink-500/10 rounded-full blur-3xl animate-pulse delay-2000"></div>
      </div>

      <div className="relative z-10 p-6 max-w-7xl mx-auto">
        <Header />
        <TabNavigation activeTab={activeTab} setActiveTab={setActiveTab} />

        {/* Controls */}
        {activeTab === 'order' && (
          <OrderControls
            models={models}
            selectedModel={selectedModel}
            setSelectedModel={setSelectedModel}
            selectedLanguage={selectedLanguage}
            setSelectedLanguage={setSelectedLanguage}
            languages={languages}
            listening={listening}
            startRecording={handleStartRecording}
            stopRecording={handleStopRecording}
            processOrder={handleProcessOrder}
            generatePDF={handleGeneratePDF}
            transcript={transcript}
            orderData={orderData}
            loading={loading}
          />
        )}

        {/* Menu Management Tab */}
        {activeTab === 'menu' && (
          <MenuManagement
            menuItems={menuItems}
            addMenuItem={addMenuItem}
            removeMenuItem={removeMenuItem}
          />
        )}

        {/* Order Display */}
        {activeTab === 'order' && (
          <OrderDisplay
            listening={listening}
            transcript={transcript}
            orderData={orderData}
            editingOrder={editingOrder}
            editedItems={editedItems}
            menuItems={menuItems}
            startOrderEditing={startOrderEditing}
            updateItemQuantity={updateItemQuantity}
            removeItemFromOrder={removeItemFromOrder}
            saveOrderChanges={saveOrderChanges}
            cancelOrderEditing={cancelOrderEditing}
          />
        )}
      </div>
    </div>
  );
}

export default App; 